import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGooglePay } from '../hooks/useGooglePay';
import { setGooglePayUnavailable } from '../app/reducers/paymentMethod';
import { selectLanguage } from '../app/reducers/language';

export default function GooglePayButton({ onToken }) {
  const { isEnvironmentValid, environment, sdkReady, sdkError, allowedCardNetworks, isReadyToPay, loadPaymentData } = useGooglePay();
  const dispatch = useDispatch();
  const language = useSelector(selectLanguage);
  const buttonLocale = (typeof language === 'string' && language.trim())
    ? language.trim().slice(0, 2).toLowerCase()
    : undefined;

  const containerRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const [status, setStatus] = useState('checking');
  const available = status === 'available';
  const [buttonReady, setButtonReady] = useState(false);
  const unavailableDispatchedRef = useRef(false);
  const onGooglePayClickedRef = useRef(null);
  const allowedCardNetworksRef = useRef(allowedCardNetworks);

  useEffect(() => { onTokenRef.current = onToken; }, [onToken]);
  useEffect(() => { allowedCardNetworksRef.current = allowedCardNetworks; }, [allowedCardNetworks]);

  const onGooglePayClicked = useCallback(async () => {
    const result = await loadPaymentData();
    if (!result || !result.token) {
      return;
    }
    if (typeof onTokenRef.current === 'function') {
      onTokenRef.current(result.token, result.cardInfo);
    }
  }, [loadPaymentData]);

  useEffect(() => { onGooglePayClickedRef.current = onGooglePayClicked; }, [onGooglePayClicked]);

  useEffect(() => {
    let active = true;
    const checkAvailability = async () => {
      try {
        if (!isEnvironmentValid) {
          if (active) setStatus('unavailable');
          return;
        }
        if (sdkError) {
          if (active) setStatus('unavailable');
          return;
        }
        if (!sdkReady) {
          if (active) setStatus('checking');
          return;
        }
        const ready = await isReadyToPay();
        if (active) setStatus(ready ? 'available' : 'unavailable');
      } catch (e) {
        console.error('GooglePayButton.checkAvailability error', e);
        if (active) setStatus('unavailable');
      }
    };
    checkAvailability();
    return () => { active = false; };
  }, [isEnvironmentValid, sdkReady, sdkError, isReadyToPay]);

  useEffect(() => {
    if (status === 'unavailable' && !unavailableDispatchedRef.current) {
      unavailableDispatchedRef.current = true;
      dispatch(setGooglePayUnavailable());
    }
  }, [status, dispatch]);

  useEffect(() => {
    if (!available || !sdkReady || containerRef.current === null) return undefined;
    const container = containerRef.current;
    setButtonReady(false);
    let button = null;
    try {
      const g = window['google'];
      const client = new g.payments.api.PaymentsClient({
        environment: environment
      });
      button = client.createButton({
        onClick: () => {
          if (typeof onGooglePayClickedRef.current === 'function') {
            onGooglePayClickedRef.current();
          }
        },
        buttonSizeMode: 'fill',
        ...(buttonLocale ? { buttonLocale } : {}),
        allowedPaymentMethods: [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY'],
              allowedCardNetworks: allowedCardNetworksRef.current
            }
          }
        ]
      });
      container.appendChild(button);
      setButtonReady(true);
    } catch (e) {
      console.error('GooglePayButton createButton error', e);
    }
    return () => {
      if (button && button.parentNode === container) {
        container.removeChild(button);
      }
    };
  }, [available, sdkReady, buttonLocale, environment]);

  if (status === 'unavailable') {
    return null;
  }

  return (
    <div className="google-pay-button-wrapper">
      <div
        ref={containerRef}
        className={"google-pay-button-container " + (buttonReady ? "is-ready" : "is-loading")}
      ></div>
    </div>
  );
}
